import axios, { AxiosError } from 'axios';

/**
 * Cliente HTTP para o fluxo de conclusão de cadastro de usuários criados
 * pelo admin (convite). Diferente do `registrationApi.ts` (auto-cadastro
 * com RegistrationRequest + código por e-mail), aqui o User já existe e
 * chega via link de confirmação do Devise (confirmation_token), que o
 * backend redireciona para `/confirmar-cadastro?token=...`.
 *
 * Público (sem sessão) — não usa o cliente `services/api.ts`.
 */
const BASE_URL = import.meta.env.VITE_API_CUSTOMER_URL || '';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { Accept: 'application/json' },
  withCredentials: false,
  timeout: 30000,
});

const PREFIX = '/api/v1/account_confirmation';

export class AccountConfirmationApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'AccountConfirmationApiError';
    this.status = status;
    this.code = code;
  }
}

const DEFAULT_ERROR = 'Não foi possível concluir a operação. Tente novamente.';

const KNOWN_ERRORS: Record<string, string> = {
  invalid_token: 'Este link de confirmação é inválido ou já foi utilizado.',
};

interface RawErrorBody {
  error?: string;
  errors?: string[];
}

function toApiError(err: unknown): AccountConfirmationApiError {
  if (axios.isAxiosError(err)) {
    const axErr = err as AxiosError<RawErrorBody>;
    if (axErr.code === 'ECONNABORTED') {
      return new AccountConfirmationApiError('A conexão expirou. Verifique sua internet e tente novamente.');
    }
    const data = axErr.response?.data;
    const rawCode = data?.error;
    const friendly = rawCode ? KNOWN_ERRORS[rawCode] : undefined;
    const message = friendly || data?.errors?.join(', ') || data?.error || DEFAULT_ERROR;
    return new AccountConfirmationApiError(message, axErr.response?.status, rawCode);
  }
  return new AccountConfirmationApiError(DEFAULT_ERROR);
}

export interface AccountConfirmationInfo {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface RawAccountConfirmationInfo {
  valid: boolean;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
}

/** Valida o token e retorna os dados já conhecidos do usuário (se houver). */
export async function fetchAccountConfirmation(token: string): Promise<AccountConfirmationInfo> {
  try {
    const { data } = await client.get<RawAccountConfirmationInfo>(PREFIX, { params: { token } });
    return {
      email: data.email,
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      phoneNumber: data.phone_number || '',
    };
  } catch (err) {
    throw toApiError(err);
  }
}

export interface CompleteAccountConfirmationParams {
  token: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  password: string;
  passwordConfirmation: string;
}

/** Conclui o cadastro: define nome/telefone/senha e confirma a conta. */
export async function completeAccountConfirmation(params: CompleteAccountConfirmationParams): Promise<void> {
  try {
    await client.post(PREFIX, {
      token: params.token,
      first_name: params.firstName,
      last_name: params.lastName,
      phone_number: params.phoneNumber,
      password: params.password,
      password_confirmation: params.passwordConfirmation,
    });
  } catch (err) {
    throw toApiError(err);
  }
}
